/* import __COLOCATED_TEMPLATE__ from './data-export.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { DrillInSummary } from './custom/report/underlying-data-modal';
import { action } from '@ember/object';
import type { FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import Range from 'embercom/models/reporting/range';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { inject as service } from '@ember/service';
import { compact, flatten, isEmpty } from 'underscore';
import type { FilterItem } from 'embercom/objects/reporting/unified/metrics/types';
import type { Attribute } from 'embercom/objects/reporting/unified/datasets/types';
import { changeFilter } from 'embercom/lib/reporting/custom/filter-helpers';
import type {
  LogicalFilter,
  LogicalFilterOperator,
} from 'embercom/components/reporting/custom/filters';
import { buildFiltersForDatasetConfig } from 'embercom/lib/reporting/custom/data-config-builder-helpers';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { groupDropDownItems } from './custom/chart-builder/chart-builder-helpers';
import type IntlService from 'embercom/services/intl';
import type { DropDownItem } from './custom/chart-builder/chart-builder-helpers';
import type ReportingCsvExport from 'embercom/services/reporting-csv-export';
import type Store from '@ember-data/store';
import {
  SchedulePreset,
  type DatasetExportConfig,
} from 'embercom/models/reporting/dataset-export-schedule';
import { cached } from 'tracked-toolbox';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';

interface GroupedDropDownItem {
  heading: string;
  items: DropDownItem[];
}

interface Args {}

export default class DataExport extends Component<Args> {
  @service router: any;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare appService: AppService;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare intl: IntlService;
  @service declare reportingCsvExport: ReportingCsvExport;
  @service declare store: Store;
  @service declare intercomEventService: any;
  @service declare modalService: any;
  @service declare datasetExportScheduleService: DatasetExportScheduleService;

  @tracked filters: LogicalFilter = {
    type: 'and',
    filters: [],
  };
  @tracked columns: any[] = this.getDefaultColumns();
  @tracked summary: DrillInSummary = {
    retrievedCount: 0,
    totalCount: 0,
    isRestricted: true,
  };

  @tracked sortBy = this.getDefaultSortBy();
  @tracked sortDirection: 'asc' | 'desc' = 'desc';
  @tracked timezone = this.appTimezone;
  @tracked pageFrom = 0;
  @tracked datasetId = 'conversation';
  @tracked isNewExportSideDrawerOpen = false;
  @tracked isManageExportSideDrawerOpen = false;

  previousSelection = this.columns;
  selectedDropdownItems = this.columns;

  get appTimezone() {
    return this.appService.app.timezone;
  }

  @cached
  get exportDateRange(): Range {
    return Range.createFromPreset('past_4_weeks', this.timezone);
  }

  get conversationAttributeDescriptors() {
    return this.store.peekAll('conversation-attributes/descriptor');
  }

  getDefaultColumns() {
    let cachedSelection = this.reportingUnderlyingDataService.loadSelectedDatasetColumnFromCache(
      this.datasetId,
    );
    if (cachedSelection.length > 0) {
      return cachedSelection;
    }

    return this.getTablePropertiesFromDatasetAttributes([])
      .filter((property) => {
        return (
          !property.value.includes('custom_data') &&
          !(
            property.value.includes('conversation_custom_fields') &&
            property.group !== 'system.attribute'
          ) &&
          !property.value.includes('tickets.data')
        );
      })
      .map((property) => property.value);
  }

  getDefaultSortBy() {
    return this.reportingUnderlyingDataService.getDatasetTimeAttribute(this.datasetId);
  }

  @action
  setDatasetId(datasetId: string) {
    this.datasetId = datasetId;
    this.sortBy = this.getDefaultSortBy();
    this.columns = this.getDefaultColumns();
    this.resetPageFrom();
    this.resetFilters();
    this.intercomEventService.trackAnalyticsEvent({
      action: 'dataset_changed',
      object: 'reporting_dataset_export',
      dataset_id: this.datasetId,
    });
  }

  resetFilters() {
    let compatibleFilters = this.filters.filters.every((filter) => {
      let properties = this.filterableDatasetProperties;
      return properties.some((property) => property.id === filter.data.attribute);
    });

    if (!compatibleFilters) {
      this.filters = {
        type: 'and',
        filters: [],
      };
    }
  }

  @action
  updateDrillInSummary(summary: DrillInSummary) {
    this.summary = summary;
  }

  @action
  onSortUpdate(sortBy: string, sortDirection: 'asc' | 'desc') {
    this.sortBy = sortBy;
    this.sortDirection = sortDirection;
  }

  get rowsPerPage() {
    return 100;
  }

  @action
  resetPageFrom() {
    this.pageFrom = 0;
  }

  @action
  updatePageFrom(pages: number) {
    this.pageFrom += pages;
  }

  private getTablePropertiesFromDatasetAttributes(currentColumns: string[] = this.columns) {
    let attributes = this.reportingMetrics.getDatasetAttributesFor(this.datasetId);

    return compact([...attributes]).map((attribute: Attribute) => {
      return {
        text: this.reportingMetrics.translateKey(attribute.name) || '',
        value: attribute.id,
        isSelected: currentColumns.indexOf(attribute.id) !== -1,
        icon: attribute.icon,
        headerName: this.reportingMetrics.translateKey(attribute.headerName) || undefined,
        group: attribute.group,
      };
    });
  }

  get tableProperties(): FilterItem[] {
    return this.getTablePropertiesFromDatasetAttributes();
  }

  get filterableDatasetProperties() {
    let properties = this.reportingMetrics.getDatasetAttributesFor(this.datasetId);

    properties = flatten([
      ...properties,
      this.reportingMetrics.getFilterableDatasetCdaAttributesFor(this.datasetId),
    ]);
    return properties;
  }

  @action
  onFiltersChanged(
    index: number,
    attribute: { id: string; field: string },
    values: string[],
    operator: string | null,
  ) {
    this.filters = changeFilter(this.filters, index, attribute, values, operator);
    this.resetPageFrom();
    this.applyDateRange(values);
  }

  applyDateRange(dateRange: any) {
    this.exportDateRange.selectedRange = dateRange.selectedRange;

    if (dateRange.selectedRange === 'custom') {
      this.exportDateRange.updateRange(dateRange.startMoment, dateRange.endMoment);
    }
  }

  @action
  onFiltersCleared() {
    this.filters = {
      type: 'and',
      filters: [],
    };
    this.resetPageFrom();
  }

  @action
  applyLogicalFilterOperator(operator: LogicalFilterOperator) {
    this.filters = {
      type: operator,
      filters: this.filters.filters,
    };
  }

  get hasData() {
    return this.summary.totalCount > 0;
  }

  get groupedTableProperties() {
    let allAttributes = this.reportingMetrics.getDatasetAttributesFor(this.datasetId);
    let groupedProperties = groupDropDownItems(
      this.tableProperties as DropDownItem[],
      allAttributes,
      this.intl,
    );

    return [...groupedProperties];
  }

  get allColumns(): GroupedDropDownItem[] {
    return this.groupedTableProperties;
  }

  get filteredAllColumns(): GroupedDropDownItem[] {
    return this.filteredGroupedTableProperties;
  }

  get filteredAllProperties() {
    return this.tableProperties.filter((property) => {
      return (
        !property.value.includes('custom_data') &&
        !(
          property.value.includes('conversation_custom_fields') &&
          property.group !== 'system.attribute'
        ) &&
        !property.value.includes('tickets.data')
      );
    });
  }

  get filteredGroupedTableProperties(): GroupedDropDownItem[] {
    let allAttributes = this.reportingMetrics.getDatasetAttributesFor(this.datasetId);

    let groupedProperties = groupDropDownItems(
      this.filteredAllProperties as DropDownItem[],
      allAttributes,
      this.intl,
    );

    return [...groupedProperties];
  }

  get shouldDisableScheduleButton() {
    return (
      this.summary.isRestricted || isEmpty(this.exportDateRange.getSelectedRangeFromCurrentTime())
    );
  }

  get shouldDisableScheduleButtonTooltip() {
    return !this.shouldDisableScheduleButton; // we show tooltip only in case button is disabled
  }

  @action
  onCloseColumnSelector() {
    if (this.previousSelection !== this.selectedDropdownItems) {
      this.setColumns(this.selectedDropdownItems);
      this.resetPageFrom();
    }
  }

  @action
  setColumns(columns: string[]) {
    this.columns = this.reportingUnderlyingDataService.getOrderedDatasetAttributeIds(
      columns,
      this.previousSelection,
      this.datasetId,
    );

    this.reportingUnderlyingDataService.setDatasetColumnSelectionInCache(
      this.datasetId,
      this.columns,
    );

    this.previousSelection = this.columns;
  }

  @action
  onSelectionChange(selection: string[]) {
    this.previousSelection = this.selectedDropdownItems;
    this.selectedDropdownItems = selection.uniq();
  }

  get countSummary() {
    return this.intl.t('reporting.data-table.view-summary', {
      currentCount: this.intl.formatNumber(this.summary.retrievedCount),
      totalCount: this.intl.formatNumber(this.summary.totalCount),
    });
  }

  get isWithinRowLimit() {
    return this.summary.totalCount <= 10_000_000;
  }

  get exportTimeRange(): FlexibleQueryTimeRange {
    let dataModel;

    let datasetTimeAttribute =
      this.reportingUnderlyingDataService
        .getDatasetTimeAttribute(this.datasetId)
        ?.split('.')
        .pop() || 'first_user_conversation_part_created_at';

    return {
      start: this.exportDateRange.startMoment.valueOf(),
      end: this.exportDateRange.endMoment.valueOf(),
      interval: this.exportDateRange.interval,
      property: datasetTimeAttribute,
      data_model: dataModel,
    };
  }

  get sendViaMail() {
    return this.summary.totalCount > 10_000;
  }

  @action
  exportCsv() {
    let analyticsEvent = { object: 'reporting_dataset_export', rows: this.summary.totalCount };
    this.reportingCsvExport.exportCsv(
      this.datasetId,
      this.filters,
      this.columns,
      this.exportTimeRange,
      this.sortBy,
      this.sortDirection,
      false,
      analyticsEvent,
      this.timezone,
      this.sendViaMail,
    );
  }

  @action
  onHoverExportButton() {
    if (this.summary.totalCount > 10_000_000) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'hovered_disabled_export_button',
        object: 'reporting_dataset_export',
        row_count: this.summary.totalCount,
      });
    } else if (this.summary.totalCount === 0) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'hovered_disabled_export_button',
        object: 'reporting_dataset_export',
        row_count: this.summary.totalCount,
      });
    }
  }

  get exportButtonTooltip() {
    if (this.summary.totalCount > 10_000_000) {
      return this.intl.t(
        'components.reporting.custom.report.data-table.export-csv-limit-exceeded-tooltip',
      );
    } else {
      return this.intl.t('reporting.data-export.export-csv-tooltip');
    }
  }

  get exportConfig(): DatasetExportConfig {
    return {
      datasetId: this.datasetId,
      sortByAttributeId: this.sortBy,
      attributeIds: this.columns,
      filter: buildFiltersForDatasetConfig(this.datasetId, this.filters),
      dateRangeSelection: this.exportDateRange.getSelectedRangeFromCurrentTime() || 'past_4_weeks',
    };
  }

  get schedule() {
    return (
      this.store
        .peekAll('reporting/dataset-export-schedule')
        .rejectBy('isDeleted', true)
        .findBy('isNew', true) || this.defaultSchedule()
    );
  }

  private defaultSchedule() {
    let model = this.store.createRecord('reporting/dataset-export-schedule');
    let exportRule = this.datasetExportScheduleService.exportRuleFrom(
      SchedulePreset.DAILY,
      this.timezone,
    );
    model.set('name', '');
    model.set('exportRule', exportRule);
    model.set('exportConfig', this.exportConfig);
    return model;
  }

  @action
  openNewExportSideDrawer() {
    this.isNewExportSideDrawerOpen = true;
  }

  @action
  openManageExportSideDrawer() {
    this.isManageExportSideDrawerOpen = true;
  }

  @action
  closeNewExportSideDrawer() {
    this.isNewExportSideDrawerOpen = false;
  }

  @action
  closeManageExportSideDrawer() {
    this.isManageExportSideDrawerOpen = false;
  }

  @action
  transitionToOldDataExport() {
    this.router.transitionTo('apps.app.reports.customer-support.export');
    this.intercomEventService.trackAnalyticsEvent({
      action: 'transition_to_old_data_export',
      object: 'customer_support_export',
    });
  }

  get scheduleOptions() {
    return [
      {
        items: [
          {
            text: this.intl.t('components.reporting.custom.report.data-table.new-schedule-export'),
            onSelectItem: this.openNewExportSideDrawer,
          },
          {
            text: this.intl.t(
              'components.reporting.custom.report.data-table.manage-schedule-export',
            ),
            onSelectItem: this.openManageExportSideDrawer,
          },
        ],
      },
    ];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::DataExport': typeof DataExport;
    'reporting/data-export': typeof DataExport;
  }
}
